import React from 'react';
import { FeedbackCommentProps, FeedbackSectionId, UserType } from './utils';
import { Answer } from './api';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import FeedbackComment from './FeedbackComment';
import FeedbackCommentTextarea from './FeedbackCommentTextarea';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import styled from 'styled-components';
import { isCustomQuestion, parseCustomQuestionId } from './api';
import { ApolloClient, FetchResult } from 'apollo-boost';
import { SuccessResponse } from 'npo/pages/ReportsBeta/NewReport/api';

const removeStart = (number: number) => {
  const numberStr = number.toString();
  if (isCustomQuestion(numberStr)) {
    return parseCustomQuestionId(numberStr);
  }
  return number;
};

export interface FeedbackCommentsSectionProps {
  hideModelButton?: boolean;
  comments: FeedbackCommentProps[];
  userType: UserType;
  currentUserId?: string;
  details?: string;
  currentUserEmail?: string;
  userName: string;
  reportId: number;
  questionId: number;
  feedbackSectionId: FeedbackSectionId;
  answer: Answer;
  commentsWrapperClassName?: string;
  textareaWrapperClassName?: string;
  deleteQuestionLevelComment: (
    client: ApolloClient<object>,
    commentId: number,
    comment: string,
    reportId: number | string,
    sectionId: FeedbackSectionId,
  ) => Promise<FetchResult<SuccessResponse<'deleteComment'>>>;
  updateQuestionLevelComment: (
    client: ApolloClient<object>,
    commentId: number,
    comment: string,
    reportId: number | string,
    sectionId: FeedbackSectionId,
  ) => Promise<FetchResult<SuccessResponse<'updateComment'>>>;
  onSuccessDelete: (questionId: number, index: number) => unknown;
  onSuccessSave: (
    questionId: number,
    comment: FeedbackCommentProps,
    index?: number,
  ) => unknown;
  addOrUpdateComment: (
    questionId: number,
    comment: FeedbackCommentProps,
    index?: number,
  ) => unknown;
}

const StyledDiv = styled.div`
  .comments-wrapper {
    padding-bottom: ${SPACERS.large};
    .comments {
      margin-left: 40px;
      margin-right: 40px;
      color: ${COLORS.whiteBase};
    }
  }
`;

const FeedbackCommentsSection: React.FC<FeedbackCommentsSectionProps> = ({
  hideModelButton,
  comments,
  userType,
  currentUserId,
  details,
  currentUserEmail,
  userName,
  reportId,
  questionId,
  feedbackSectionId,
  answer,
  commentsWrapperClassName,
  textareaWrapperClassName,
  onSuccessSave,
  onSuccessDelete,
  deleteQuestionLevelComment,
  updateQuestionLevelComment,
  addOrUpdateComment,
}) => {
  return (
    <StyledDiv className="comments-wrapper">
      <div className="comments">
        {comments.map((c, i) => {
          const id =
            feedbackSectionId === 'custom_questions'
              ? removeStart(questionId)
              : questionId;
          return (
            <FeedbackComment
              index={i}
              comment={c}
              key={c.commentId}
              className={commentsWrapperClassName}
              reportId={reportId}
              currentUserType={userType}
              currentUserName={userName}
              sectionId={feedbackSectionId}
              hideModelButton={hideModelButton}
              currentUserEmail={currentUserEmail}
              handleSave={updateQuestionLevelComment}
              handleDelete={deleteQuestionLevelComment}
              onSuccessDelete={index => onSuccessDelete(id, index)}
              onSuccessSave={(comment, index) =>
                onSuccessSave(questionId, comment, index)
              }
            />
          );
        })}
      </div>
      {userType !== 'npo' && (
        <FeedbackCommentTextarea
          userType={userType}
          marginLeft={SPACERS.extraLarge}
          marginRight={SPACERS.extraLarge}
          currentUserId={currentUserId}
          currentUserEmail={currentUserEmail}
          userName={userName}
          reportId={reportId}
          questionId={questionId}
          sectionId={feedbackSectionId}
          answer={answer.text}
          details={details}
          className={textareaWrapperClassName}
          hideModelButton={hideModelButton}
          onSaveSuccess={addOrUpdateComment}
        />
      )}
    </StyledDiv>
  );
};

export default FeedbackCommentsSection;
